import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, DollarSign, Car, Home, Briefcase, CreditCard } from 'lucide-react';

const PainSection = () => {
  // Define a common animation for the icons
  const iconAnimation = {
    rotateY: 360, // Rotate around the Y-axis for a left-to-right roll
  };

  // Define a common transition for the icons
  const iconTransition = {
    duration: 10, // 10 seconds for one full rotation
    ease: "linear", // Constant speed
    repeat: Infinity, // Loop indefinitely
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 text-center"
    >
      <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-6">
        Why Does Bad Credit Hurt So Much?
      </h2>
      <ul className="space-y-4 text-lg text-gray-300 text-left">
        {/* Apply glow to each list item if they are considered "small boxes" */}
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50" // Added background and border for the glow to apply to
        >
          {/* Neon Glow Effect for each list item */}
          <motion.div
            className="absolute inset-0 rounded-xl" // No negative margin, just inset
            style={{
              background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', // Pearl white gradient
              filter: 'blur(8px)', // Soft glow
              zIndex: -1, // Behind the main content
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', // Mask to create border effect
              WebkitMaskComposite: 'xor', // Combine masks
              maskComposite: 'exclude', // Standard for exclude
            }}
            animate={{ opacity: [0.2, 0.5, 0.2] }} // Slow flicker
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1" // Container for the icon
          >
            <XCircle className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Higher Interest Rates:</strong> Lenders see you as a bigger risk, so they charge more for loans and credit cards. It's like paying extra for a seat on a plane just because you *might* cause turbulence.
          </p>
        </motion.li>
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50"
        >
          <motion.div
            className="absolute inset-0 rounded-xl"
            style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1"
          >
            <DollarSign className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Difficulty Getting Loans:</strong> Mortgages, car loans, personal loans – they all become a Herculean task. You might as well try to convince a cat to take a bath.
          </p>
        </motion.li>
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50"
        >
          <motion.div
            className="absolute inset-0 rounded-xl"
            style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(8px)', zIndex: -1, WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1"
          >
            <CreditCard className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Limited Credit Card Options:</strong> Forget those juicy rewards cards. You'll be stuck with high-fee, low-limit cards that make you feel like you're still in high school.
          </p>
        </motion.li>
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50"
        >
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1"
          >
            <Car className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Higher Insurance Premiums:</strong> Yes, even your car insurance can be affected. Apparently, bad credit makes you a more reckless driver in the eyes of actuaries. Who knew?
          </p>
        </motion.li>
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 1.1 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50"
        >
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1"
          >
            <Home className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Trouble Renting an Apartment:</strong> Landlords often check credit. Your dream apartment might just slip through your fingers like sand in an hourglass.
          </p>
        </motion.li>
        <motion.li
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          className="relative flex items-start bg-gray-900/50 p-4 rounded-xl border border-gray-700/50"
        >
          <motion.div
            animate={iconAnimation}
            transition={iconTransition}
            className="flex-shrink-0 mr-3 mt-1"
          >
            <Briefcase className="text-red-500" size={24} />
          </motion.div>
          <p className="font-light">
            <strong className="text-white font-medium">Employment Challenges:</strong> Some employers check credit, especially for positions involving financial responsibility. So, your credit score could literally stand between you and that corner office.
          </p>
        </motion.li>
      </ul>
    </motion.div>
  );
};

export default PainSection;